import { useEffect, useRef, useState, type FormEvent } from "react"
import {
  portalItemThreadFor,
  type PortalClientFile,
  type PortalRequestResponse,
} from "@ledgerbox/domain"

import type { PortalApi } from "./api"
import { usePortalApi } from "./api-context"
import { formatDateTime, plural } from "./format"
import { LoadingRegion, ThreadsSkeleton } from "./skeleton"
import {
  attachableFiles,
  threadAwaitsClient,
  threadRowName,
} from "./thread-rows"
import { InlineError, RetryNotice } from "./ui"

type ThreadList = Awaited<ReturnType<PortalApi["listThreads"]>>
type ThreadDetail = Awaited<ReturnType<PortalApi["getThread"]>>

export function PortalThreads({
  files,
  requests,
  firmName,
  clientName,
}: {
  files: PortalClientFile[]
  requests: PortalRequestResponse[]
  firmName: string
  clientName: string
}) {
  const api = usePortalApi()
  const [threads, setThreads] = useState<ThreadList["threads"]>([])
  const [active, setActive] = useState<ThreadDetail | null>(null)
  const [subject, setSubject] = useState("")
  const [body, setBody] = useState("")
  const [newAttachments, setNewAttachments] = useState<string[]>([])
  const [anchor, setAnchor] = useState("")
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = async () => {
    setLoadError(null)
    try {
      const data = await api.listThreads()
      setThreads(data.threads)
    } catch {
      setLoadError("Your messages could not be loaded.")
      throw new Error("Your messages could not be loaded.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh().catch(() => undefined)
  }, [])

  const create = async (event: FormEvent) => {
    event.preventDefault()
    setPending(true)
    setError(null)
    try {
      const detail = await api.createThread({
        subject,
        body,
        requestId: anchor.startsWith("request:")
          ? anchor.slice("request:".length)
          : null,
        portalFileId: anchor.startsWith("file:")
          ? anchor.slice("file:".length)
          : null,
        attachmentIds: newAttachments,
      })
      setActive(detail)
      setSubject("")
      setBody("")
      setNewAttachments([])
      setAnchor("")
      await refresh().catch(() => undefined)
    } catch {
      setError("Your message could not be sent. Try again.")
    } finally {
      setPending(false)
    }
  }

  const heading = (
    <div className="page-heading section-gap">
      <h1>Messages</h1>
      <p className="lede">
        Ask {firmName} about something on your list, or about a file you sent.
      </p>
    </div>
  )

  if (loading)
    return (
      <>
        {heading}
        <LoadingRegion label="Loading messages">
          <ThreadsSkeleton />
        </LoadingRegion>
      </>
    )
  if (loadError && !threads.length)
    return (
      <RetryNotice
        message={loadError}
        onRetry={() => {
          setLoading(true)
          void refresh().catch(() => undefined)
        }}
      />
    )

  if (active) {
    return (
      <ThreadView
        detail={active}
        files={files}
        requests={requests}
        firmName={firmName}
        clientName={clientName}
        onBack={() => setActive(null)}
        onDetail={(next) => {
          setActive(next)
          void refresh().catch(() => undefined)
        }}
      />
    )
  }

  return (
    <>
      {heading}
      <div className="messages-layout">
        <section aria-label="Message threads">
          {threads.length ? (
            <div className="thread-list">
              {threads.map((thread) => {
                const isNew = threadAwaitsClient(thread)
                return (
                <button
                  type="button"
                  key={thread.id}
                  className={isNew ? "is-new" : undefined}
                  onClick={() =>
                    void api
                      .getThread(thread.id)
                      .then(setActive)
                      .catch(() =>
                        setError("This message could not be loaded.")
                      )
                  }
                >
                  <span className="thread-row-name">
                    {/* The Messages tab counts these as new (R3-U10). */}
                    {isNew ? (
                      <>
                        <span className="thread-new-dot" aria-hidden="true" />
                        <span className="visually-hidden">New reply: </span>
                      </>
                    ) : null}
                    {threadRowName(thread, requests)}
                  </span>
                  <span className="thread-row-preview">
                    {thread.lastMessagePreview ??
                      `${thread.messageCount} ${plural(thread.messageCount, "message", "messages")}`}
                  </span>
                  {thread.lastMessageAt ? (
                    <time>{formatDateTime(thread.lastMessageAt)}</time>
                  ) : null}
                </button>
                )
              })}
            </div>
          ) : (
            <div className="empty-state view-empty">
              <strong>No messages yet</strong>
              <span>
                Ask {firmName} a question with the form on this page. Their
                reply shows up here.
              </span>
            </div>
          )}
          {error ? <InlineError message={error} /> : null}
        </section>
        <form className="new-thread" onSubmit={create}>
          <h2>New message</h2>
          <label htmlFor="thread-subject">Subject</label>
          <input
            id="thread-subject"
            value={subject}
            maxLength={160}
            onChange={(event) => setSubject(event.currentTarget.value)}
          />
          <label htmlFor="thread-body">Message</label>
          <textarea
            id="thread-body"
            value={body}
            maxLength={5000}
            onChange={(event) => setBody(event.currentTarget.value)}
          />
          <label htmlFor="thread-anchor">About (optional)</label>
          <select
            id="thread-anchor"
            value={anchor}
            onChange={(event) => setAnchor(event.currentTarget.value)}
          >
            <option value="">General question</option>
            {requests.map((request) => (
              <option
                key={request.requestId}
                value={`request:${request.requestId}`}
              >
                Request: {request.periodLabel}
              </option>
            ))}
            {files.map((file) => (
              <option key={file.fileId} value={`file:${file.fileId}`}>
                File: {file.fileName}
              </option>
            ))}
          </select>
          <AttachmentPicker
            files={files}
            selected={newAttachments}
            onChange={setNewAttachments}
          />
          <p>
            Everyone at {clientName} and {firmName} can read this thread.
          </p>
          <button
            className="accent-pill"
            disabled={pending || !subject.trim() || !body.trim()}
          >
            {pending ? "Sending…" : "Send message"}
          </button>
        </form>
      </div>
    </>
  )
}

function ThreadView({
  detail,
  files,
  requests,
  firmName,
  clientName,
  onBack,
  onDetail,
}: {
  detail: ThreadDetail
  files: PortalClientFile[]
  requests: PortalRequestResponse[]
  firmName: string
  clientName: string
  onBack(): void
  onDetail(next: ThreadDetail): void
}) {
  const api = usePortalApi()
  const [reply, setReply] = useState("")
  const [replyAttachments, setReplyAttachments] = useState<string[]>([])
  const [showAttachments, setShowAttachments] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const feedEnd = useRef<HTMLDivElement>(null)

  useEffect(() => {
    feedEnd.current?.scrollIntoView({ block: "end" })
  }, [detail.messages.length])

  const anchorRequest = requests.find(
    (candidate) => candidate.requestId === detail.thread.requestId
  )
  const anchorFile = files.find(
    (candidate) => candidate.fileId === detail.thread.portalFileId
  )
  const anchorLabel = anchorRequest
    ? anchorRequest.periodLabel
    : anchorFile
      ? anchorFile.fileName
      : null

  const sendReply = async (event: FormEvent) => {
    event.preventDefault()
    setPending(true)
    setError(null)
    try {
      onDetail(
        await api.postThreadMessage(detail.thread.id, reply, replyAttachments)
      )
      setReply("")
      setReplyAttachments([])
      setShowAttachments(false)
    } catch {
      setError("Your reply could not be sent. Try again.")
    } finally {
      setPending(false)
    }
  }

  return (
    <section className="thread-view" aria-label={detail.thread.subject}>
      <div className="thread-head">
        <button
          type="button"
          className="icon-button"
          aria-label="Back to all messages"
          onClick={onBack}
        >
          <svg viewBox="0 0 24 24">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <div className="thread-head-main">
          <div className="thread-title">
            {threadRowName(detail.thread, requests)}
          </div>
          <div className="thread-sub">
            Thread with {firmName}
            {anchorLabel ? ` · ${anchorLabel}` : ""}
          </div>
        </div>
      </div>
      <div className="thread-feed">
        <p className="visibility">
          Everyone at {clientName} and {firmName} can read this thread.
        </p>
        {detail.messages.map((message) => (
          <ThreadMessage key={message.id} message={message} firmName={firmName} />
        ))}
        <div ref={feedEnd} />
      </div>
      {showAttachments ? (
        <div className="compose-attachments">
          <AttachmentPicker
            files={files}
            selected={replyAttachments}
            onChange={setReplyAttachments}
          />
        </div>
      ) : null}
      {error ? <InlineError message={error} /> : null}
      <form className="compose" onSubmit={sendReply}>
        {files.some((file) => file.blobPurgedAt === null) ? (
          <button
            type="button"
            className={`compose-round${showAttachments ? " is-open" : ""}`}
            aria-expanded={showAttachments}
            aria-label={
              showAttachments
                ? "Hide the attachment list"
                : "Attach files from this portal"
            }
            onClick={() => setShowAttachments((value) => !value)}
          >
            <svg viewBox="0 0 24 24">
              <path d="M21 12l-8.5 8.5a5 5 0 0 1-7-7L14 5a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L16 7" />
            </svg>
          </button>
        ) : null}
        <textarea
          aria-label="Reply"
          placeholder="Write a message"
          value={reply}
          maxLength={5000}
          rows={1}
          onChange={(event) => setReply(event.currentTarget.value)}
        />
        <button
          type="submit"
          className="compose-round send"
          aria-label="Send reply"
          disabled={pending || !reply.trim()}
        >
          <svg viewBox="0 0 24 24">
            <path d="M4 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      </form>
    </section>
  )
}

/** One message: who wrote it and when, the words, and what it carries. */
function ThreadMessage({
  message,
  firmName,
}: {
  message: ThreadDetail["messages"][number]
  firmName: string
}) {
  const client = message.authorSide === "client"
  return (
    <article className={`msg ${client ? "is-client" : "is-firm"}`}>
      <span className="who">
        {client ? "You" : firmName} · {formatDateTime(message.createdAt)}
      </span>
      <div className="bubble">{message.body}</div>
      {message.attachments.map((attachment) => (
        <span className="attach" key={attachment.fileId}>
          <svg viewBox="0 0 24 24">
            <path d="M6 3h9l4 4v14H6z" />
          </svg>
          <span>
            {attachment.available
              ? attachment.fileName
              : "Attachment no longer available"}
          </span>
        </span>
      ))}
    </article>
  )
}

/**
 * The client's threads, read once for a page of items. Each item finds its
 * own thread in the list; `refresh` reads it again after a message.
 */
export type ItemThreads = ReturnType<typeof useItemThreads>

export function useItemThreads() {
  const api = usePortalApi()
  const [threads, setThreads] = useState<ThreadList["threads"]>([])
  const refresh = async () => {
    try {
      setThreads((await api.listThreads()).threads)
    } catch {
      // A list that did not load leaves each item with its ask box only.
    }
  }
  useEffect(() => {
    void refresh()
  }, [api])
  return {
    threadFor: (itemId: string) => portalItemThreadFor(threads, itemId),
    refresh,
  }
}

/**
 * The conversation about one item, under the item: what was said, and a box
 * to ask about it. The first question starts a thread titled with the
 * item's name; it also lists under Messages. `collapsed` starts it as a
 * text button, for a list where every item is open at once.
 */
export function ItemThread({
  item,
  thread,
  firmName,
  collapsed = false,
  onPosted,
}: {
  item: PortalRequestResponse["items"][number]
  thread: ThreadList["threads"][number] | null
  firmName: string
  collapsed?: boolean
  onPosted(): void
}) {
  const api = usePortalApi()
  const [open, setOpen] = useState(!collapsed)
  const [detail, setDetail] = useState<ThreadDetail | null>(null)
  const [body, setBody] = useState("")
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fieldId = `item-ask-${item.itemId}`
  const threadId = thread?.id ?? null
  const lastMessageAt = thread?.lastMessageAt ?? null

  useEffect(() => {
    if (!open || !threadId) return
    let active = true
    void api
      .getThread(threadId)
      .then((next) => {
        if (active) setDetail(next)
      })
      .catch(() => {
        if (active) setError("The messages about this item could not be loaded.")
      })
    return () => {
      active = false
    }
  }, [api, open, threadId, lastMessageAt])

  const send = async (event: FormEvent) => {
    event.preventDefault()
    setPending(true)
    setError(null)
    try {
      setDetail(
        threadId
          ? await api.postThreadMessage(threadId, body.trim())
          : await api.createThread({
              subject: item.name,
              body: body.trim(),
              requestItemId: item.itemId,
            })
      )
      setBody("")
      onPosted()
    } catch {
      setError("Your message could not be sent. Try again.")
    } finally {
      setPending(false)
    }
  }

  if (!open) {
    const count = thread?.messageCount ?? 0
    return (
      <button
        type="button"
        className="text-button item-thread-open"
        aria-expanded={false}
        onClick={() => setOpen(true)}
      >
        {count
          ? `${count} ${plural(count, "message", "messages")} about this item`
          : "Ask about this item"}
      </button>
    )
  }

  return (
    <section className="item-thread" aria-label={`Messages about ${item.name}`}>
      {detail?.messages.length ? (
        <div className="item-thread-feed">
          {detail.messages.map((message) => (
            <ThreadMessage
              key={message.id}
              message={message}
              firmName={firmName}
            />
          ))}
        </div>
      ) : null}
      <form className="item-ask" onSubmit={(event) => void send(event)}>
        <label htmlFor={fieldId}>Ask about this item</label>
        <textarea
          id={fieldId}
          // Opened from its text button, the box takes the focus the
          // button had.
          autoFocus={collapsed}
          rows={2}
          maxLength={5000}
          value={body}
          onChange={(event) => setBody(event.currentTarget.value)}
        />
        <button
          type="submit"
          className="ghost-pill"
          disabled={pending || !body.trim()}
        >
          {pending ? "Sending…" : "Send"}
        </button>
      </form>
      {error ? <InlineError message={error} /> : null}
    </section>
  )
}

function AttachmentPicker({
  files,
  selected,
  onChange,
}: {
  files: PortalClientFile[]
  selected: string[]
  onChange(next: string[]): void
}) {
  // Each file once, told apart by when it came in (R3-U11).
  const available = attachableFiles(files, formatDateTime)
  if (!available.length) return null
  return (
    <fieldset className="attachment-picker">
      <legend>Attach files already in this portal</legend>
      {available.map((file) => (
        <label key={file.fileId}>
          <input
            type="checkbox"
            checked={selected.includes(file.fileId)}
            disabled={!selected.includes(file.fileId) && selected.length >= 10}
            onChange={(event) =>
              onChange(
                event.currentTarget.checked
                  ? [...selected, file.fileId]
                  : selected.filter((fileId) => fileId !== file.fileId)
              )
            }
          />
          <span>{file.label}</span>
        </label>
      ))}
    </fieldset>
  )
}
